import React, { useEffect } from "react";
import { TimePicker, Switch } from "antd";
import dayjs from "dayjs";
import GenericTable from "../GenericTable";
import "./style.css";

const DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const blankOperatingHours = () =>
  DAYS_OF_WEEK.map((day) => ({
    day,
    startTime: null,
    endTime: null,
    isClosed: false,
    is24Hours: false,
  }));

const GenericOperatingHours = ({ value = [], onChange, disabled = false }) => {
  const hours = value.length ? value : !disabled ? blankOperatingHours() : value;

  useEffect(() => {
    if (!disabled && !value.length) {
      onChange?.(blankOperatingHours());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, value.length]);

  const applyUpdate = (day, changes) => {
    const updated = hours.map((row) =>
      row.day === day ? { ...row, ...changes } : row,
    );

    onChange?.(updated);
  };

  return (
    <div className="operating-hours">
      <GenericTable
        title="Operating Hours"
        dataSource={hours.map((row) => ({ ...row, key: row.day }))} // ensure unique reference per row
        columns={[
          {
            title: "Day",
            dataIndex: "day",
            key: "day",
          },
          {
            title: "Start Time",
            dataIndex: "startTime",
            key: "startTime",
            render: (_, row) => (
              <TimePicker
                value={row.startTime ? dayjs(row.startTime, "HH:mm") : null}
                format="hh:mm A"
                disabled={disabled || row.isClosed || row.is24Hours}
                onChange={(time) =>
                  applyUpdate(row.day, {
                    startTime: time?.format("HH:mm") || null,
                  })
                }
              />
            ),
          },
          {
            title: "End Time",
            dataIndex: "endTime",
            key: "endTime",
            render: (_, row) => (
              <TimePicker
                value={row.endTime ? dayjs(row.endTime, "HH:mm") : null}
                format="hh:mm A"
                disabled={row.isClosed || row.is24Hours || disabled}
                onChange={(time) =>
                  applyUpdate(row.day, {
                    endTime: time?.format("HH:mm") || null,
                  })
                }
              />
            ),
          },
          {
            title: "24 Hours",
            dataIndex: "is24Hours",
            key: "is24Hours",
            render: (_, row) => (
              <Switch
                className="switch-24hours"
                checked={row.is24Hours}
                disabled={row.isClosed || disabled}
                onChange={(val) =>
                  applyUpdate(row.day, {
                    is24Hours: val,
                    isClosed: val ? false : row.isClosed,
                    startTime: val ? null : row.startTime,
                    endTime: val ? null : row.endTime,
                  })
                }
              />
            ),
          },
          {
            title: "Closed",
            dataIndex: "isClosed",
            key: "isClosed",
            render: (_, row) => (
              <Switch
                className="switch-closed"
                checked={row.isClosed}
                disabled={row.is24Hours || disabled}
                onChange={(val) =>
                  applyUpdate(row.day, {
                    isClosed: val,
                    is24Hours: val ? false : row.is24Hours,
                    startTime: val ? null : row.startTime,
                    endTime: val ? null : row.endTime,
                  })
                }
              />
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_, row) => {
              if (row.isClosed)
                return <span className="status-tag status-closed">Closed</span>;
              if (row.is24Hours)
                return (
                  <span className="status-tag status-24hours">24 Hours</span>
                );
              return <span className="status-tag status-open">Open</span>;
            },
          },
        ]}
      />
    </div>
  );
};

export default GenericOperatingHours;
